import {Component, DestroyRef, effect, inject, input, NgZone, OnInit} from '@angular/core';
import {DOCUMENT, NgFor, NgIf} from '@angular/common';
import {RouterLink} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {NGXLogger} from 'ngx-logger';
import {environment} from '../../../environments/environment';
import {MobileFooterSheetComponent} from './mobile-footer-sheet/mobile-footer-sheet.component';
import {NativeService} from '../../_service/native.service';

type MobileNavigationType = 'HOUSE' | 'ANALYSIS' | 'SCORER' | 'OPEN_HOUSE' | 'SERVICE';

interface MobileNavigationItem {
  type: MobileNavigationType;
  label: string;
  icon: string;
  activeIcon: string;
  activeKeys: string[];
}

interface MobileSheetLink {
  icon: string;
  name: string;
  group?: string;
  path: string;
  queryParams: Record<string, string>;
  type?: MobileNavigationType;
  staticPage?: boolean;
}

interface MobileSheetConfig {
  title: string;
  description: string;
  links: MobileSheetLink[];
}

@Component({
  selector: 'app-bottom',
  standalone: true,
  imports: [NgFor, NgIf, RouterLink],
  templateUrl: './bottom.component.html',
  styleUrl: './bottom.component.scss'
})
export class BottomComponent implements OnInit {
  currentUrl = input.required<string>();
  activeMobileFooterIcon = '';
  hide = false;
  showMyShortcut = true;
  scrollHidden = false;
  readonly nativeIos: boolean;
  private readonly document = inject(DOCUMENT);
  private readonly view = this.document.defaultView;
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private lastScrollY = 0;
  private downwardDistance = 0;
  private scrollFrame: number | undefined;
  private menuOpen = false;

  readonly navigationItems: MobileNavigationItem[] = [
    {
      type: 'HOUSE',
      label: '등기변경',
      icon: 'assets/img/menu/m-menu-01.svg',
      activeIcon: 'assets/img/menu/m-menu-01-f.svg',
      activeKeys: ['HOUSE_REGISTER', 'MY_HOUSE']
    },
    {
      type: 'ANALYSIS',
      label: '권리분석',
      icon: 'assets/img/menu/m-menu-02.svg',
      activeIcon: 'assets/img/menu/m-menu-02-f.svg',
      activeKeys: ['ANALYSIS']
    },
    {
      type: 'SCORER',
      label: '시세검색',
      icon: 'assets/img/menu/m-menu-03.svg',
      activeIcon: 'assets/img/menu/m-menu-03-f.svg',
      activeKeys: ['SCORER']
    },
    {
      type: 'OPEN_HOUSE',
      label: '사업자 서비스',
      icon: 'assets/img/menu/m-menu-04.svg',
      activeIcon: 'assets/img/menu/m-menu-04-f.svg',
      activeKeys: ['OPEN_HOUSE', 'BUSINESS_ANALYSIS']
    },
    {
      type: 'SERVICE',
      label: '더보기',
      icon: 'assets/img/menu/m-menu-05.svg',
      activeIcon: 'assets/img/menu/m-menu-05-f.svg',
      activeKeys: ['SERVICE']
    }
  ];

  private readonly sheetConfigs: Record<MobileNavigationType, MobileSheetConfig> = {
    ANALYSIS: {
      title: '권리분석',
      description: '분석 보고서를 확인하거나 새 분석을 시작해보세요.',
      links: [
        {icon: 'assets/img/menu/m-menu-02-01.svg', name: '권리 분석 보고서', path: '/analysis/list', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-02-02.svg', name: '신규 분석', path: '/analysis', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-02-03.svg', name: '쿠폰 구입', path: '/coupon', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-02-04.svg', name: '권리분석 소개', path: '/analysis/info', queryParams: {}}
      ]
    },
    HOUSE: {
      title: '등기변경',
      description: '내 부동산의 등기 변경을 빠르게 확인하세요.',
      links: [
        {icon: 'assets/img/menu/m-menu-01-01.svg', name: '등기변경 리스트', path: '/house/list', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-01-02.svg', name: '서비스 신청', path: '/house/register', queryParams: {type: 'MY_HOUSE'}},
        {icon: 'assets/img/menu/m-menu-01-03.svg', name: '서비스 일괄 신청', path: '/house/batch', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-01-04.svg', name: '서비스 일괄 연장', path: '/house/extends', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-01-05.svg', name: '쿠폰 구입', path: '/coupon', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-01-06.svg', name: '서비스 안내', path: '/info/my-house', queryParams: {}}
      ]
    },
    OPEN_HOUSE: {
      title: '사업자 서비스',
      description: environment.production
        ? '사업자 권리분석과 오픈부동산 서비스를 확인하세요.'
        : '사업자 권리분석과 소유자 모니터링, 오픈부동산 서비스를 확인하세요.',
      links: [
        ...(environment.production ? [] : [
          {icon: 'assets/img/menu/m-menu-02-01.svg', name: '소유자 변경 모니터링', path: '/owner-monitoring', queryParams: {}},
          {icon: 'assets/img/menu/m-menu-02-04.svg', name: '소유자 변경 모니터링 소개', path: '/info/owner-monitoring', queryParams: {}}
        ]),
        {icon: 'assets/img/menu/m-menu-02-05.svg', name: '사업자 권리분석 소개', path: '/info/business-analysis', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-02-06.svg', name: '사업자 권리분석 미리보기', path: '/info/business-analysis-preview', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-04-01.svg', name: '오픈 부동산 알림신청', path: '/house/open', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-04-02.svg', name: '오픈 부동산 등록', path: '/house/register', queryParams: {type: 'OPEN_HOUSE'}},
        {icon: 'assets/img/menu/m-menu-04-03.svg', name: '서비스 일괄 연장', path: '/house/extends', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-04-04.svg', name: '쿠폰 구입', path: '/coupon', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-04-05.svg', name: '서비스 안내', path: '/info/open-house', queryParams: {}}
      ]
    },
    SCORER: {
      title: '시세검색',
      description: 'AI로 부동산 시세와 변화를 확인해보세요.',
      links: [
        {icon: 'assets/img/menu/m-menu-03-01.svg', name: '시세 검색', path: '/scorer', queryParams: {}},
        {
          icon: 'assets/img/menu/m-menu-02-01.svg',
          name: '부동산 리포트',
          path: '/report/monthly',
          queryParams: {}
        },
        {icon: 'assets/img/menu/m-menu-03-02.svg', name: 'AI 시세검색 소개', path: '/info/scorer', queryParams: {}}
      ]
    },
    SERVICE: {
      title: '더보기',
      description: '체크팀의 서비스와 고객지원 메뉴입니다.',
      links: [
        {icon: 'assets/img/menu/m-menu-05-02.svg', name: '부동산 가이드', group: '가이드', path: '/guides/index.html', queryParams: {}, staticPage: true},
        {icon: 'assets/img/service/etc/ic_detail-main.svg', name: '부동산 계산기', group: '서비스', path: '/real-estate-calculator', queryParams: {}},
        {icon: 'assets/img/menu/m-menu-05-01.svg', name: '이용금액', group: '이용 안내', path: '/info/pricing', queryParams: {type: ''}},
        {icon: 'assets/img/menu/m-menu-05-02.svg', name: '자주하는 질문', group: '이용 안내', path: '/info/faq', queryParams: {type: ''}},
        {icon: 'assets/img/menu/m-menu-05-03.svg', name: 'API', group: '이용 안내', path: '/info/api', queryParams: {type: ''}},
        {icon: 'assets/img/menu/m-menu-05-04.svg', name: '이벤트', group: '소식', path: '/info/events', queryParams: {type: 'EVENT'}},
        {icon: 'assets/img/menu/m-menu-05-05.svg', name: '공지사항', group: '소식', path: '/info/notice', queryParams: {type: 'NOTICE'}},
        {icon: 'assets/img/menu/m-menu-05-06.svg', name: '1:1 문의', group: '고객지원', path: '/inquire', queryParams: {type: 'INQUIRE'}},
        {icon: 'assets/img/menu/m-menu-05-07.svg', name: '오류신고', group: '고객지원', path: '/inquire', queryParams: {type: 'ERROR'}},
        {icon: 'assets/img/menu/m-menu-05-08.svg', name: '기능요청', group: '고객지원', path: '/inquire', queryParams: {type: 'SUGGEST'}},
        {icon: 'assets/img/menu/m-menu-05-09.svg', name: '회사소개', group: '회사', path: '/info/company', queryParams: {}}
      ]
    }
  };

  constructor(private readonly log: NGXLogger,
              nativeService: NativeService,
              private readonly matBottomSheet: MatBottomSheet) {
    this.nativeIos = nativeService.isNativeiOS();

    effect(() => {
      const url = this.currentUrl();
      this.checkMobileFooterIcon(url);
      this.hide = this.shouldHide(url);
      this.showMyShortcut = this.shouldShowMyShortcut(url);
      this.showFooter();
    });
  }

  ngOnInit(): void {
    const view = this.view;
    if (!view) return;

    this.lastScrollY = this.scrollPosition();
    this.zone.runOutsideAngular(() => view.addEventListener('scroll', this.onScroll, {passive: true}));
    this.destroyRef.onDestroy(() => {
      view.removeEventListener('scroll', this.onScroll);
      this.clearScrollWork();
    });
  }

  showFooter(): void {
    this.clearScrollWork();
    this.lastScrollY = this.scrollPosition();
    this.downwardDistance = 0;
    this.setScrollHidden(false);
  }

  private readonly onScroll = (): void => {
    const view = this.view;
    if (!view || this.hide || this.scrollFrame !== undefined) return;

    this.scrollFrame = view.requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      const position = this.scrollPosition();
      const delta = position - this.lastScrollY;
      this.lastScrollY = position;

      if (position <= 24 || delta < 0) {
        this.downwardDistance = 0;
        this.setScrollHidden(false);
      } else if (delta > 0) {
        this.downwardDistance += delta;
        if (this.downwardDistance >= 8) this.setScrollHidden(true);
      }

      // 스크롤을 멈춰도 숨김 상태를 유지하고, 위로 스크롤할 때 다시 표시한다.
    });
  };

  private scrollPosition(): number {
    if (!this.view) return 0;
    // iOS의 상·하단 탄성 스크롤은 방향 전환으로 해석하지 않는다.
    const maximum = Math.max(0, this.document.documentElement.scrollHeight - this.view.innerHeight);
    return Math.max(0, Math.min(this.view.scrollY, maximum));
  }

  private setScrollHidden(hidden: boolean): void {
    const focused = this.document.activeElement;
    if (hidden && (this.menuOpen || (focused?.closest('.mobile-bottom-nav') && focused.matches(':focus-visible')))) return;
    if (this.scrollHidden === hidden) return;
    this.zone.run(() => this.scrollHidden = hidden);
  }

  private clearScrollWork(): void {
    if (!this.view) return;
    if (this.scrollFrame !== undefined) this.view.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = undefined;
  }

  private shouldShowMyShortcut(url: string): boolean {
    const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
    return !(path === '/my-real-estate' || path.startsWith('/my-real-estate/') ||
      path === '/home' || path.startsWith('/home/') ||
      path === '/design-preview/my-real-estate' || path.startsWith('/design-preview/home'));
  }

  isActive(item: MobileNavigationItem): boolean {
    return item.activeKeys.includes(this.activeMobileFooterIcon);
  }

  openBottomSheet(type: MobileNavigationType): void {
    this.showFooter();
    this.menuOpen = true;
    const config = this.sheetConfigs[type];
    const links = config.links.map(link => ({...link, type}));

    this.matBottomSheet.open(MobileFooterSheetComponent, {
      data: {...config, links},
      panelClass: 'mobile-footer-sheet-panel',
      backdropClass: 'mobile-footer-sheet-backdrop'
    }).afterDismissed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.menuOpen = false;
      this.showFooter();
      this.log.debug(response);
    });
  }

  private shouldHide(url: string): boolean {
    const path = url.split('?')[0];

    if (path === '/house/extends' || path === '/house/extends/' ||
      path === '/house/renewal' || path === '/house/renewal/') {
      return true;
    }

    // 권리분석 수집·입력 흐름에서는 화면 하단의 단계 UI에 집중할 수 있도록 모바일 공통 메뉴를 숨긴다.
    if (url.startsWith('/house/register') || url.includes('/analysis/detail') || path === '/analysis/loading' || path === '/design-preview/analysis-collecting' || path === '/design-preview/analysis-detail') {
      return true;
    }

    if (path.startsWith('/design-preview/analysis-step-')) {
      return true;
    }

    if (!url.includes('/analysis')) {
      return false;
    }

    return new URLSearchParams(url.split('?')[1] ?? '').get('hide') === 'true';
  }

  private checkMobileFooterIcon(currentUrl: string): void {
    const path = currentUrl.split(/[?#]/)[0];
    if (currentUrl === '/house/list' || currentUrl.includes('/house/detail') || currentUrl.includes('/house/map') || currentUrl.includes('/house/statistics') || currentUrl.includes('/info/my-house')) {
      this.activeMobileFooterIcon = 'MY_HOUSE';
    // 시세검색 화면은 다른 서비스의 쿼리 파라미터가 남아 있어도 현재 경로를 우선해 판별한다.
    } else if (currentUrl.includes('/scorer') || currentUrl.includes('/info/scorer') || path === '/report' || path.startsWith('/report/')) {
      this.activeMobileFooterIcon = 'SCORER';
    } else if (currentUrl.startsWith('/info/business-analysis') || currentUrl.includes('/owner-monitoring')) {
      this.activeMobileFooterIcon = 'BUSINESS_ANALYSIS';
    } else if (currentUrl.includes('/house/open') || currentUrl.includes('OPEN_HOUSE') || currentUrl.includes('/info/open-house')) {
      this.activeMobileFooterIcon = 'OPEN_HOUSE';
    } else if (currentUrl.includes('/house/extends')) {
      this.activeMobileFooterIcon = '';
    } else if (currentUrl.includes('/house')) {
      this.activeMobileFooterIcon = 'HOUSE_REGISTER';
    } else if (currentUrl.includes('/home')) {
      this.activeMobileFooterIcon = 'HOME';
    } else if (currentUrl.includes('/analysis')) {
      this.activeMobileFooterIcon = 'ANALYSIS';
    } else if (currentUrl.includes('/info') || currentUrl.includes('/inquire')) {
      this.activeMobileFooterIcon = 'SERVICE';
    } else {
      this.activeMobileFooterIcon = '';
    }
  }
}
